<template>
  <MainLayout>
    <div class="parallax" data-parallax data-parallax-speed="0.25" data-parallax-direction="down">
      <div class="relative w-screen">
        <img
          class="absolute -left-[1vw] top-[-12vh] w-[20vw] opacity-80"
          src="/images/bg-visual-left-top.webp"
          alt="" />
        <img
          class="absolute -right-[6vw] top-[18vh] w-[30vw] opacity-60"
          src="/images/bg-visual-right-top.webp"
          alt="" />
      </div>
    </div>

    <section id="hero" class="max-w-[1080px] lg:m-[auto]">
      <div class="flex items-center lg:mt-[4rem] lg:-mb-[3rem]">
        <div class="flex flex-col gap-y-4.5 lg:gap-y-8">
          <h1
            class="text-[32px] leading-[36px] lg:text-[70px] lg:leading-[74px] font-bold text-dark text-center font-markPro lg:max-w-[20ch] tracking-[-0.1rem]">
            {{ t('pages.info.integrations.hero.heading.1') }}
            <span class="text-gradient-primary whitespace-nowrap">{{
              t('pages.info.integrations.hero.heading.2')
            }}</span>
            {{ t('pages.info.integrations.hero.heading.3') }}
          </h1>

          <div class="flex flex-col text-center leading-[27px] lg:text-lg font-semibold m-auto">
            <p class="text-dark">{{ t('pages.info.integrations.hero.description.1') }}</p>
            <p class="text-lighter">{{ t('pages.info.integrations.hero.description.2') }}</p>
          </div>
        </div>
      </div>
    </section>

    <section id="integrations" class="relative flex justify-center">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
        <RouterLink :to="{ name: 'integrations.lightspeed' }" class="group relative overflow-hidden rounded-lg">
          <img class="h-full w-full transition-transform duration-300 group-hover:scale-105" src="/images/integrations/lightspeed.webp" alt="lightspeed" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/0 group-hover:from-black/10 to-transparent transition-all duration-300 flex items-end justify-center pb-4">
            <span class="text-white font-semibold text-sm bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-4 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-x-1.5 mb-1.5">{{ t('main.integration.more_info') }} <i class="bi bi-arrow-right"></i></span>
          </div>
        </RouterLink>
        <RouterLink :to="{ name: 'integrations.stripe' }" class="group relative overflow-hidden rounded-lg">
          <img class="h-full w-full transition-transform duration-300 group-hover:scale-105" src="/images/integrations/stripe.webp" alt="stripe" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/0 group-hover:from-black/10 to-transparent transition-all duration-300 flex items-end justify-center pb-4">
            <span class="text-white font-semibold text-sm bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-4 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-x-1.5 mb-1.5">{{ t('main.integration.more_info') }} <i class="bi bi-arrow-right"></i></span>
          </div>
        </RouterLink>
        <RouterLink :to="{ name: 'integrations.mpluskassa' }" class="group relative overflow-hidden rounded-lg">
          <img class="h-full w-full transition-transform duration-300 group-hover:scale-105" src="/images/integrations/mpluskassa.webp" alt="M+ Kassa" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/0 group-hover:from-black/10 to-transparent transition-all duration-300 flex items-end justify-center pb-4">
            <span class="text-white font-semibold text-sm bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-4 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-x-1.5 mb-1.5">{{ t('main.integration.more_info') }} <i class="bi bi-arrow-right"></i></span>
          </div>
        </RouterLink>
        <RouterLink :to="{ name: 'integrations.exact' }" class="group relative overflow-hidden rounded-lg">
          <img class="h-full w-full transition-transform duration-300 group-hover:scale-105" src="/images/integrations/exact.webp" alt="Exact Online" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/0 group-hover:from-black/10 to-transparent transition-all duration-300 flex items-end justify-center pb-4">
            <span class="text-white font-semibold text-sm bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-4 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-x-1.5 mb-1.5">{{ t('main.integration.more_info') }} <i class="bi bi-arrow-right"></i></span>
          </div>
        </RouterLink>
        <RouterLink :to="{ name: 'integrations.google' }" class="group relative overflow-hidden rounded-lg">
          <img class="h-full w-full transition-transform duration-300 group-hover:scale-105" src="/images/integrations/google.webp" alt="Google" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/0 group-hover:from-black/10 to-transparent transition-all duration-300 flex items-end justify-center pb-4">
            <span class="text-white font-semibold text-sm bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-4 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-x-1.5 mb-1.5">{{ t('main.integration.more_info') }} <i class="bi bi-arrow-right"></i></span>
          </div>
        </RouterLink>
        <RouterLink :to="{ name: 'integrations.winstonpos' }" class="group relative overflow-hidden rounded-lg">
          <img class="h-full w-full transition-transform duration-300 group-hover:scale-105" src="/images/integrations/winstonpos.webp" alt="WinstonPOS" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/0 group-hover:from-black/10 to-transparent transition-all duration-300 flex items-end justify-center pb-4">
            <span class="text-white font-semibold text-sm bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-4 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-x-1.5 mb-1.5">{{ t('main.integration.more_info') }} <i class="bi bi-arrow-right"></i></span>
          </div>
        </RouterLink>
        <RouterLink :to="{ name: 'integrations.untill' }" class="group relative overflow-hidden rounded-lg">
          <img class="h-full w-full transition-transform duration-300 group-hover:scale-105" src="/images/integrations/untill.webp" alt="unTill" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/0 group-hover:from-black/10 to-transparent transition-all duration-300 flex items-end justify-center pb-4">
            <span class="text-white font-semibold text-sm bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-4 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-x-1.5 mb-1.5">{{ t('main.integration.more_info') }} <i class="bi bi-arrow-right"></i></span>
          </div>
        </RouterLink>
        <RouterLink :to="{ name: 'integrations.mollie' }" class="group relative overflow-hidden rounded-lg">
          <img class="h-full w-full transition-transform duration-300 group-hover:scale-105" src="/images/integrations/mollie.webp" alt="Mollie" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/0 group-hover:from-black/10 to-transparent transition-all duration-300 flex items-end justify-center pb-4">
            <span class="text-white font-semibold text-sm bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-4 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-x-1.5 mb-1.5">{{ t('main.integration.more_info') }} <i class="bi bi-arrow-right"></i></span>
          </div>
        </RouterLink>
        <RouterLink :to="{ name: 'integrations.twilio' }" class="group relative overflow-hidden rounded-lg">
          <img class="h-full w-full transition-transform duration-300 group-hover:scale-105" src="/images/integrations/twilio.webp" alt="Twilio" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/0 group-hover:from-black/10 to-transparent transition-all duration-300 flex items-end justify-center pb-4">
            <span class="text-white font-semibold text-sm bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-4 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-x-1.5 mb-1.5">{{ t('main.integration.more_info') }} <i class="bi bi-arrow-right"></i></span>
          </div>
        </RouterLink>
        <RouterLink :to="{ name: 'integrations.facebook' }" class="group relative overflow-hidden rounded-lg">
          <img class="h-full w-full transition-transform duration-300 group-hover:scale-105" src="/images/integrations/facebook.webp" alt="Facebook" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/0 group-hover:from-black/10 to-transparent transition-all duration-300 flex items-end justify-center pb-4">
            <span class="text-white font-semibold text-sm bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-4 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-x-1.5 mb-1.5">{{ t('main.integration.more_info') }} <i class="bi bi-arrow-right"></i></span>
          </div>
        </RouterLink>
        <RouterLink :to="{ name: 'integrations.cashdesk' }" class="group relative overflow-hidden rounded-lg">
          <img class="h-full w-full transition-transform duration-300 group-hover:scale-105" src="/images/integrations/cashdesk.webp" alt="Cashdesk" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/0 group-hover:from-black/10 to-transparent transition-all duration-300 flex items-end justify-center pb-4">
            <span class="text-white font-semibold text-sm bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-4 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-x-1.5 mb-1.5">{{ t('main.integration.more_info') }} <i class="bi bi-arrow-right"></i></span>
          </div>
        </RouterLink>
        <RouterLink :to="{ name: 'integrations.leat' }" class="group relative overflow-hidden rounded-lg">
          <img class="h-full w-full transition-transform duration-300 group-hover:scale-105" src="/images/integrations/leat.webp" alt="LEAT" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/0 group-hover:from-black/10 to-transparent transition-all duration-300 flex items-end justify-center pb-4">
            <span class="text-white font-semibold text-sm bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-4 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-x-1.5 mb-1.5">{{ t('main.integration.more_info') }} <i class="bi bi-arrow-right"></i></span>
          </div>
        </RouterLink>
        <RouterLink :to="{ name: 'integrations.instagram' }" class="group relative overflow-hidden rounded-lg">
          <img class="h-full w-full transition-transform duration-300 group-hover:scale-105" src="/images/integrations/instagram.webp" alt="Instagram" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/0 group-hover:from-black/10 to-transparent transition-all duration-300 flex items-end justify-center pb-4">
            <span class="text-white font-semibold text-sm bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-4 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-x-1.5 mb-1.5">{{ t('main.integration.more_info') }} <i class="bi bi-arrow-right"></i></span>
          </div>
        </RouterLink>
        <RouterLink :to="{ name: 'integrations.slack' }" class="group relative overflow-hidden rounded-lg">
          <img class="h-full w-full transition-transform duration-300 group-hover:scale-105" src="/images/integrations/slack.webp" alt="Slack" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/0 group-hover:from-black/10 to-transparent transition-all duration-300 flex items-end justify-center pb-4">
            <span class="text-white font-semibold text-sm bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-4 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-x-1.5 mb-1.5">{{ t('main.integration.more_info') }} <i class="bi bi-arrow-right"></i></span>
          </div>
        </RouterLink>
        <div
          class="col-span-2 bg-white rounded-lg drop-shadow-xl max-md:order-1 m-4 !mt-2.5 !mb-4.5 px-10 py-10">
          <h4 class="font-markPro text-2xl font-bold tracking-[-0.04rem]">
            {{ t('pages.info.integrations.partner.title') }}
          </h4>
          <p class="text-lighter font-medium max-w-[450px] mt-2">
            {{ t('pages.info.integrations.partner.text.1') }}
            <RouterLink :to="{ name: 'info.support' }">
              <u>{{ t('pages.info.integrations.partner.text.2') }}</u>
            </RouterLink>
            {{ t('pages.info.integrations.partner.text.3') }}
          </p>
        </div>
      </div>
    </section>

    <section id="flow">
      <div class="grid grid-cols-1 md:grid-cols-12 gap-8 max-w-[1200px]">
        <div class="md:col-span-8 row-span-3 bg-white rounded-lg drop-shadow max-md:order-1">
          <div class="flex flex-col justify-between h-full">
            <div class="flex flex-col gap-y-4 p-8 pb-0">
              <h4 class="text-gradient-primary font-markPro text-2xl font-bold tracking-[-0.04rem]">
                {{ t('pages.info.integrations.cards.card_1.title') }}
              </h4>
              <p class="text-lighter font-medium max-w-[450px]">
                {{ t('pages.info.integrations.cards.card_1.text') }}
              </p>
              <a
                v-track-event="'onboarding_start'"
                href="https://merchant.bistroo.nl/register" 
                class="btn btn-light mt-4">
                {{ t('pages.info.integrations.cards.card_1.button') }}</a>
            </div>

            <div class="flex justify-end items-end mt-10 max-md:ml-14 lg:mt-[-3.4rem] pointer-events-none">
              <img src="/images/img-merchantportal-blocks.webp" alt="Merchant portal" width="450" />
            </div>
          </div>
        </div>

        <div
          class="md:col-span-4 row-span-3 bg-white rounded-lg p-8 drop-shadow flex flex-col gap-y-4 justify-between max-md:order-4">
          <div class="flex flex-col gap-y-4">
            <h4 class="font-markPro text-2xl font-bold tracking-[-0.04rem]">
              {{ t('pages.info.integrations.cards.card_2.title') }}
            </h4>

            <p class="text-lighter">{{ t('pages.info.integrations.cards.card_2.text') }}</p>
          </div>
        </div>

        <div class="md:col-span-4 row-span-2 bg-white rounded-lg drop-shadow max-md:order-2">
          <div class="flex flex-col justify-between h-full">
            <div class="flex flex-col gap-y-4 p-8">
              <h4 class="font-markPro text-2xl font-bold tracking-[-0.04rem]">
                {{ t('pages.info.integrations.cards.card_3.title') }}
              </h4>
              <p class="text-lighter">{{ t('pages.info.integrations.cards.card_3.text') }}</p>
            </div>
          </div>
        </div>

        <div class="md:col-span-4 row-span-2 bg-white rounded-lg drop-shadow max-md:order-3">
          <div class="flex flex-col justify-between h-full">
            <div class="flex flex-col gap-y-4 p-8">
              <h4 class="font-markPro text-gradient-primary text-2xl font-bold tracking-[-0.04rem]">
                {{ t('pages.info.integrations.cards.card_4.title') }}
              </h4>
              <p class="text-lighter">{{ t('pages.info.integrations.cards.card_4.text') }}</p>
            </div>
          </div>
        </div>

        <div class="md:col-span-4 row-span-2 bg-white rounded-lg p-8 drop-shadow max-md:order-5">
          <div class="flex flex-col gap-y-4">
            <h4 class="font-markPro text-2xl font-bold tracking-[-0.04rem]">
              {{ t('pages.info.integrations.cards.card_5.title') }}
            </h4>
            <p class="text-lighter">{{ t('pages.info.integrations.cards.card_5.text') }}</p>
          </div>
        </div>
      </div>
    </section>

    <section id="cta" class="backgroundgradient backgroundgradient-bottom">
      <div class="w-full rounded-2xl py-16 max-md:px-14 lg:pt-26 lg:pb-20 relative overflow-hidden">
        <div class="absolute inset-0 z-10 bg-[#E3EEF7]"></div>
        <div
          class="parallax"
          data-parallax
          data-parallax-speed="0.05"
          data-parallax-direction="down">
          <div class="relative w-full h-full">
            <img
              class="absolute top-0 left-0 -translate-x-1/3 h-[150%] rotate-[45%]"
              src="/images/bg-visual-left-top.webp"
              alt="" />

            <img
              class="absolute right-0 translate-x-1/3 -top-1/4 lg:-top-1/2 h-1/2 lg:h-full rotate-[-20deg]"
              src="/images/bg-visual-right-top.webp"
              alt="" />
          </div>
        </div>

        <div class="flex flex-col gap-y-5.5 justify-center items-center text-center relative z-30">
          <h4
            class="text-[32px] leading-[42px] lg:text-5xl font-bold text-dark text-center font-markPro block tracking-[-0.08rem]">
            {{ t('main.cta_block.open_your_webshop_today') }}
            <span class="text-gradient-primary whitespace-nowrap">
              {{ t('main.cta_block.its_free') }}
            </span>
          </h4>

          <p class="text-lg lg:text-[24px] text-lighter font-semibold">
            {{ t('main.cta_block.start_generating_more_orders') }}
          </p>

          <a
            v-track-event="'onboarding_start'"
            href="https://merchant.bistroo.nl/register"
            class="btn btn-lg btn-primary flex items-center gap-x-3 mt-4">
            <span>
              {{ t('main.cta_block.get_started_for_free') }}
            </span>
            <i class="bi bi-arrow-right text-lg mt-[3px]"></i>
          </a>
        </div>
      </div>
    </section>
  </MainLayout>
</template>

<script setup lang="ts">
import MainLayout from '@/layouts/MainLayout.vue'
import { useHead } from '@unhead/vue'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

const { t } = useI18n()
useHead(computed(() => ({
  title: t('pages.info.integrations.seo.title'),
  meta: [{ name: 'description', content: t('pages.info.integrations.seo.description') }],
})))
</script>

<style scoped>
@reference "@/assets/css/main.css";
</style>
